import { SortingState, Table } from '@tanstack/react-table';

import { useEffect, useRef, useState } from 'react';

import { fitBounds } from '../../../modules/referenceView/Tkv2MlsMap/fitBounds.ts';
import { Feature } from '../../../modules/referenceView/Tkv2MlsMap/types.ts';
import { MAP_SOURCES, MAP_STYLES } from '../../Tkv2Map/consts.ts';
import MapBox from '../../map/MapBox.ts';
import { MAP_LAYERS, MAP_LAYER_DEFINITIONS, MAP_PIN_STATUS_FILTERS } from '../../map/constants.ts';
import { Pin, PinStatusFilter } from '../../map/types.ts';
import { useMap } from '../../map/useMap.ts';
import { propertyPinsToGeoJSON } from '../../map/usePinLayerCallbacks.ts';
import { CompSelectionProperty } from '../types.ts';

const DEFAULT_ZOOM = 14;

export function useCompMap({
  selectedPropertyId,
  lat,
  lng,
  subjectLat,
  subjectLng,
  data,
  comps,
  compSelectionTableRef,
  sorting,
  setFocusedRow,
  mapType,
}: {
  selectedPropertyId: string;
  lat: number;
  lng: number;
  subjectLat: number;
  subjectLng: number;
  data: CompSelectionProperty[];
  comps: CompSelectionProperty[];
  sorting: SortingState;
  compSelectionTableRef?: React.MutableRefObject<Table<CompSelectionProperty> | undefined>;
  setFocusedRow: (rowId: string | undefined) => void;
  mapType: string;
}) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);
  const propertyLoaded = useRef<boolean>(false);

  const [pins, setPins] = useState<Pin[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<PinStatusFilter[]>([
    MAP_PIN_STATUS_FILTERS['SELECTED COMPS'],
    MAP_PIN_STATUS_FILTERS.ACTIVE,
    MAP_PIN_STATUS_FILTERS.SOLD,
    MAP_PIN_STATUS_FILTERS.PENDING,
  ]);

  useEffect(() => {
    if (!compSelectionTableRef?.current) return;

    const { rows } = compSelectionTableRef.current.getSortedRowModel();
    const rowMap = new Map(rows.map((r, i) => [r.original.id, i]));
    const selectedRowIds = compSelectionTableRef.current?.getSelectedRowModel().rows.map(r => r.id) || [];

    const newPins = [...data, ...comps].reduce((acc, property) => {
      if (!property.latitude || !property.longitude) return acc;

      const index = rowMap.get(property.id);
      const status = (property.mlsStatus as PinStatusFilter) || MAP_PIN_STATUS_FILTERS.OTHER;
      const isComp = selectedRowIds.includes(property.id);
      acc.push({
        id: property.id,
        lat: property.latitude,
        lng: property.longitude,
        label: index !== undefined ? `${index}` : '',
        active: isComp,
        selected: property.id === selectedPropertyId,
        status: isComp ? MAP_PIN_STATUS_FILTERS['SELECTED COMPS'] : status,
        icon: status,
      });
      return acc;
    }, [] as Pin[]);

    setPins(newPins);
  }, [compSelectionTableRef, sorting, data, comps]);

  useMap({
    mapContainerRef,
    mapRef,
    center: { lat: subjectLat, lng: subjectLng },
    zoom: DEFAULT_ZOOM,
    subject: { lat: subjectLat, lng: subjectLng },
    pins,
    selectedStatuses,
    enableStatusFiltering: true,
    callbacks: {
      onStyleLoad: () => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        if (map?.getSource(MAP_SOURCES.PINS)) {
          map?.removeLayer(MAP_LAYERS.PINS);
          map?.removeSource(MAP_SOURCES.PINS);
        }
        map.addSource(MAP_SOURCES.PINS, propertyPinsToGeoJSON(pins));
        map.addLayer(MAP_LAYER_DEFINITIONS.PINS);
      },
      onPinFilterChange: (statuses: PinStatusFilter[]) => {
        setSelectedStatuses(statuses);
      },
      onPinSourceLoad: (sourceId, target) => {
        if (propertyLoaded.current || (lat === subjectLat && lng === subjectLng)) return;

        fitBounds(target, [
          { latitude: lat, longitude: lng },
          { latitude: subjectLat, longitude: subjectLng },
        ]);

        propertyLoaded.current = true;
      },
      onPinClick: (feature: Feature | undefined) => {
        if (!compSelectionTableRef?.current || !feature?.properties?.id) return;
        const pinId = feature.properties.id;

        const row = compSelectionTableRef.current.getRow(pinId);
        if (!row) return;

        setFocusedRow(row.id);
      },
    },
  });

  useEffect(() => {
    const url = mapType === 'satellite' ? MAP_STYLES.SATELLITE.url : MAP_STYLES.STREET.url;
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;
    map?.setStyle(url);
  }, [mapType]);

  return { mapContainerRef };
}
